import type { StaffProfile } from "../services/staffService";

export const STAFF_DIRECTORY_PAGE_SIZES = [25, 50, 100];

export type StaffSortKey =
  | "user_name"
  | "employee_id"
  | "position_title"
  | "department_name"
  | "hire_date"
  | "employment_status"
  | "salary";

export type StaffDirectoryFilters = {
  search: string;
  status: string;
  /** A department id, "none" for school-wide staff, or "" for everyone. */
  department: string;
  position: string;
  pay: "" | "set" | "unset";
};

export const EMPTY_STAFF_FILTERS: StaffDirectoryFilters = {
  search: "",
  status: "",
  department: "",
  position: "",
  pay: "",
};

export const STAFF_STATUS_TABS: [string, string][] = [
  ["", "All"],
  ["active", "Active"],
  ["probation", "Probation"],
  ["suspended", "Suspended"],
  ["resigned", "Resigned"],
  ["retired", "Retired"],
  ["terminated", "Terminated"],
];

export function activeStaffFilterCount(filters: StaffDirectoryFilters) {
  return Object.values(filters).filter((value) => value.trim()).length;
}

export function filterStaffProfiles(
  profiles: StaffProfile[],
  filters: StaffDirectoryFilters,
  sortKey: StaffSortKey,
  sortDirection: "asc" | "desc",
) {
  const term = filters.search.trim().toLowerCase();
  const filtered = profiles.filter((profile) => {
    const matchesSearch =
      !term ||
      [
        profile.user_name,
        profile.user_username,
        profile.employee_id,
        profile.position_title,
        profile.department_name ?? "",
        profile.work_location ?? "",
        ...(profile.headed_departments ?? []).map((department) => department.name),
      ].some((value) => value.toLowerCase().includes(term));
    const matchesDepartment =
      !filters.department ||
      (filters.department === "none"
        ? profile.department == null
        : String(profile.department ?? "") === filters.department);
    const hasPay = Boolean(profile.salary && Number(profile.salary) > 0);
    return (
      matchesSearch &&
      matchesDepartment &&
      (!filters.status || profile.employment_status === filters.status) &&
      (!filters.position || String(profile.position) === filters.position) &&
      (!filters.pay || (filters.pay === "set" ? hasPay : !hasPay))
    );
  });
  const direction = sortDirection === "asc" ? 1 : -1;
  return filtered.sort((a, b) => {
    if (sortKey === "salary") {
      return (Number(a.salary ?? 0) - Number(b.salary ?? 0)) * direction;
    }
    const left = String(a[sortKey] ?? "").toLowerCase();
    const right = String(b[sortKey] ?? "").toLowerCase();
    return left.localeCompare(right, undefined, { numeric: true }) * direction;
  });
}
